import { KeyIcon } from '@heroicons/react/24/outline'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { PasswordStrength } from '@authy/lib-common/entities'

import { Dialog } from '@/components/Dialog'
import { TextField } from '@/components/TextField'

import { PasswordRequirements } from './PasswordRequirements'

type PasswordChangeDialogProps = {
    minLength: number
    strength: PasswordStrength
    onClose: () => void
    onSubmit: (newPassword: string) => Promise<void>
}

export const PasswordChangeDialog = ({ minLength, strength, onClose, onSubmit }: PasswordChangeDialogProps) => {
    const { t } = useTranslation()

    const newPasswordInputRef = useRef<HTMLInputElement | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [localError, setLocalError] = useState<string | null>(null)
    const [form, setForm] = useState({
        newPassword: '',
        confirmPassword: '',
    })

    const confirmPasswordError = useMemo(() => {
        if (!form.confirmPassword.length || form.newPassword === form.confirmPassword) {
            return null
        }
        return t('security.local.password.dialog.confirmPasswordMismatch')
    }, [form.confirmPassword, form.newPassword, t])

    const onChange = (field: 'newPassword' | 'confirmPassword', value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setLocalError(null)
    }

    const handleSubmit = async () => {
        if (form.newPassword !== form.confirmPassword) {
            return
        }
        setSubmitting(true)
        try {
            await onSubmit(form.newPassword)
        } catch (e) {
            setLocalError(e instanceof Error ? e.message : t('security.local.password.messages.updateError'))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog
            open
            title={t('security.local.password.dialog.title')}
            onClose={onClose}
            initialFocusRef={newPasswordInputRef}
            closeLabel={t('common.close')}
            footer={
                <div className="flex flex-wrap justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="border-account-border bg-account-surface text-account-primary hover:bg-account-page rounded-lg border px-4 py-2 font-semibold transition"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            void handleSubmit()
                        }}
                        disabled={submitting || !form.newPassword || !form.confirmPassword || !!confirmPasswordError}
                        className="bg-account-accent text-account-on-accent hover:bg-account-accent-hover rounded-lg px-6 py-2 font-bold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {t('security.local.password.dialog.submit')}
                    </button>
                </div>
            }
        >
            <div className="border-account-divider bg-account-muted text-account-primary flex items-start gap-3 rounded-lg border px-4 py-3 text-sm">
                <KeyIcon className="text-account-secondary mt-0.5 h-5 w-5 shrink-0" />
                <p>{t('security.local.password.dialog.description')}</p>
            </div>

            {localError && (
                <p role="alert" className="text-account-error text-sm">
                    {localError}
                </p>
            )}

            <div className="space-y-5">
                <TextField
                    inputRef={newPasswordInputRef}
                    label={t('security.local.password.dialog.newPassword')}
                    type="password"
                    autoComplete="new-password"
                    value={form.newPassword}
                    onChange={(value) => onChange('newPassword', value)}
                />
                <PasswordRequirements password={form.newPassword} strength={strength} minLength={minLength} />
                <TextField
                    label={t('security.local.password.dialog.confirmPassword')}
                    type="password"
                    autoComplete="new-password"
                    value={form.confirmPassword}
                    error={confirmPasswordError}
                    onChange={(value) => onChange('confirmPassword', value)}
                />
            </div>
        </Dialog>
    )
}
